/**
 * Makes `$t(key, 'en')`, `$t(key, count | named, 'en')`, `$t(key, 'en', named)` and `$tc(key, count, 'en')`
 * translate into that locale.
 *
 * Job views and forms pass the content language this way (`$t(key, job.lang)`), but the composition
 * API of vue-i18n reads a string argument as a default message and has no `$tc` at all (removed in
 * v11), so those views rendered Slovak labels around English texts and `$tc` threw while rendering.
 *
 * Only the active locale's messages are loaded and there is no fallback locale. When a key is
 * missing in the requested locale (its messages aren't loaded yet, or it isn't translated), every
 * form here falls back to the active locale instead of printing the key. Pages showing content in
 * another locale load its messages first with `useContentLocaleMessages`.
 */
import type { Ref } from 'vue'

type TranslateOptions = Record<string, unknown> & { locale?: string }
type Translate = (...args: unknown[]) => string
/** The parts of the global composer used here (`localeCodes` is added by @nuxtjs/i18n). */
type LocaleComposer = {
    t: Translate
    te: (key: string, locale?: string) => boolean
    locale: Ref<string>
    localeCodes: Ref<string[]>
}

declare module 'vue' {
    interface ComponentCustomProperties {
        $tc: (key: string, count?: number, localeOrNamed?: string | Record<string, unknown>) => string
    }
}

export default defineNuxtPlugin({
    name: 'i18n-locale-argument',
    dependsOn: ['i18n:plugin'],
    setup(nuxtApp) {
        const composer = nuxtApp.$i18n as unknown as LocaleComposer
        const localeCodes = new Set<string>(composer.localeCodes.value)
        const originalT = composer.t.bind(composer)
        const hasMessage = composer.te.bind(composer)

        const isLocale = (value: unknown): value is string => typeof value === 'string' && localeCodes.has(value)
        const isOptions = (value: unknown): value is TranslateOptions =>
            !!value && typeof value === 'object' && !Array.isArray(value)

        /** The requested locale, or undefined (= active locale) when it has no message for the key. */
        const usableLocale = (key: string, locale: string) =>
            locale === composer.locale.value || hasMessage(key, locale) ? locale : undefined

        /** Named params with a numeric `count` select the plural form, as `$t(key, lang, { count })` expects. */
        const translate = (key: string, countOrNamed: unknown, locale: string) => {
            const usable = usableLocale(key, locale)
            const options = usable ? { locale: usable } : {}
            if (typeof countOrNamed === 'number') return originalT(key, countOrNamed, options)
            if (isOptions(countOrNamed) && typeof countOrNamed.count === 'number') {
                return originalT(key, countOrNamed.count, { ...options, named: countOrNamed })
            }
            return originalT(key, countOrNamed ?? {}, options)
        }

        const t: Translate = (...args) => {
            const [key, second, third] = args
            if (typeof key !== 'string') return originalT(...args)

            if (args.length === 2 && isLocale(second)) return translate(key, undefined, second)
            if (args.length === 3 && isLocale(third)) return translate(key, second, third)
            if (args.length === 3 && isLocale(second) && isOptions(third)) return translate(key, third, second)
            if (args.length === 3 && isOptions(third) && typeof third.locale === 'string') {
                if (usableLocale(key, third.locale)) return originalT(...args)
                const { locale: _unusable, ...options } = third
                return originalT(key, second, options)
            }
            return originalT(...args)
        }

        const tc = (key: string, count?: number, localeOrNamed?: string | Record<string, unknown>) => {
            if (isLocale(localeOrNamed)) return translate(key, count, localeOrNamed)
            if (count === undefined) return originalT(key)
            return isOptions(localeOrNamed) ? originalT(key, count, { named: localeOrNamed }) : originalT(key, count)
        }

        composer.t = t
        nuxtApp.vueApp.config.globalProperties.$t = t as typeof nuxtApp.vueApp.config.globalProperties.$t
        nuxtApp.vueApp.config.globalProperties.$tc = tc
    }
})
